import { useEffect, useState } from "react";
import { ProductsTypes } from "../../../../api/types/products.types";
import ProductsService from "../../../../api/services/products.service";
import { useParams } from "react-router-dom";
import styles from "./ProductDetail.module.scss";
import Loading from "../../../../components/common/loading/Loading";
import ImageGallery from "../../../../components/common/image-gallery/ImageGallery";
import Button from "../../../../components/common/button/Button";
import CartIcon from "../../../../assets/svgs/header/CartIcon";
import HeartIcon from "../../../../assets/svgs/header/HeartIcon";
import { toast } from "sonner";
import { useAuth } from "../../../../context/AuthContext";
import { useCurrencies } from "../../../../context/CurrenciesContext";
import { useBasketWishlist } from "../../../../context/BasketWishlistContext";
import FilledHeartIcon from "../../../../assets/svgs/header/FilledHeartIcon";
import Modal from "../../../../components/common/modal/Modal";
import { useLocalizedNavigate } from "../../../../hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../context/LanguageContext";
import TitleUpdater from "../../../../components/common/title/TitleUpdater";
import {
  getAttributeName,
  getAttributeValue,
} from "../../../../utils/attributeTranslations";
import RelatedProducts from "../../../../components/sections/product/related-products/RelatedProducts";
import Newsletter from "../../../../components/sections/home/newsletter/Newsletter";
import { useDeliverySettings } from "../../../../hooks/useDeliverySettings";
import { useInstallmentBanks } from "../../../../hooks/useInstallmentBanks";
import InstallmentPlans from "../../../../components/sections/product/installment-plans/InstallmentPlans";

const ProductDetail = () => {
  const [detail, setDetail] = useState<Partial<ProductsTypes>>({});
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null,
  );
  const [quantity, setQuantity] = useState(1);
  const [addLoading, setAddLoading] = useState(false);
  const [addedSuccess, setAddedSuccess] = useState(false);
  const [isInBasket, setIsInBasket] = useState(false);
  const [isWishlist, setIsWishlist] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const { t } = useTranslation("translation");
  const { id } = useParams();
  const navigate = useLocalizedNavigate();
  const { isLoggedIn } = useAuth();
  const { currencyId, currency: contextCurrency } = useCurrencies();
  const delivery = useDeliverySettings(currencyId);
  const installmentBanks = useInstallmentBanks();
  const { increaseBasket, increaseWishlist, decreaseWishlist } =
    useBasketWishlist();
  const { currentLanguage } = useLanguage();

  const getLocalizedName = (product: any) => {
    if (!product) return "";

    switch (currentLanguage) {
      case "az":
        return product.name_az || product.name_en;
      case "de":
        return product.name_de || product.name_en;
      case "es":
        return product.name_es || product.name_en;
      case "en":
      default:
        return product.name_en;
    }
  };

  const getLocalizedDescription = (product: any) => {
    if (!product) return "";

    switch (currentLanguage) {
      case "az":
        return product.description_az || product.description_en;
      case "de":
        return product.description_de || product.description_en;
      case "es":
        return product.description_es || product.description_en;
      case "en":
      default:
        return product.description_en;
    }
  };

  const checkIsInBasket = async (variantId: number | null) => {
    if (!variantId) return;
    if (!isLoggedIn) {
      const guestBasket = JSON.parse(
        localStorage.getItem("guest_basket") || "[]",
      );
      setIsInBasket(
        guestBasket.some((item: any) => item.product_variant_id === variantId),
      );
    } else {
      try {
        const res = await ProductsService.getAllBAsket(Number(currencyId));
        const items = res?.data || [];
        setIsInBasket(
          items.some((item: any) => item.product?.id === variantId),
        );
      } catch {
        setIsInBasket(false);
      }
    }
  };

  useEffect(() => {
    const getData = async () => {
      const res = await ProductsService.getSingleProduct(
        Number(id),
        Number(currencyId),
      );
      setDetail(res.data);
      if (res.data?.product_variant_list?.length) {
        const foundVariant = res.data.product_variant_list.find(
          (variant: any) => variant.id === Number(id),
        );
        setSelectedVariantId(foundVariant?.id);
        if (res.data.wishlist !== undefined) {
          setIsWishlist(res.data.wishlist);
        }
        await checkIsInBasket(foundVariant?.id ?? null);
      }
    };
    getData();
  }, [id, currencyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUrlWithVariantId = (variantId: number) => {
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(/\/\d+$/, `/${variantId}`);
    if (newPath !== currentPath) {
      window.history.pushState(null, "", newPath);
    }
  };

  const increaseQuantity = () => {
    if (selectedVariant && quantity < selectedVariant.stock_quantity) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const addToBasket = async () => {
    if (!selectedVariantId) return;
    if (!isLoggedIn) {
      if (selectedVariant?.stock_quantity === 0) {
        toast.error(t("product_out_of_stock"));
        return;
      }

      const storedBasket = localStorage.getItem("guest_basket");
      let basketData = storedBasket ? JSON.parse(storedBasket) : [];
      const existingItemIndex = basketData.findIndex(
        (item: any) => item.product_variant_id === selectedVariantId,
      );
      if (existingItemIndex !== -1) {
        basketData[existingItemIndex].quantity += quantity;
      } else {
        basketData.push({
          product_variant_id: selectedVariantId,
          quantity: quantity,
          product: {
            id: selectedVariantId,
            images: selectedVariant?.images || [],
            name_en: selectedVariant?.name_en,
            name_az: selectedVariant?.name_az,
            name_de: selectedVariant?.name_de,
            name_es: selectedVariant?.name_es,
            price: selectedVariant?.price,
            discount_price: selectedVariant?.discount_price,
            discount_percent: selectedVariant?.discount_percent,
            currency_name: contextCurrency,
            stock_quantity: selectedVariant?.stock_quantity,
            attributes: selectedVariant?.attributes || [],
          },
        });
      }
      localStorage.setItem("guest_basket", JSON.stringify(basketData));
      increaseBasket(quantity);
      setIsInBasket(true);
      setAddedSuccess(true);
      setQuantity(1);
      setTimeout(() => setAddedSuccess(false), 1500);
      return;
    }

    setAddLoading(true);
    try {
      const formData = new FormData();
      formData.append("product_variant_id", selectedVariantId.toString());
      formData.append("quantity", quantity.toString());
      await ProductsService.updateBasketAuth(Number(currencyId), formData);
      increaseBasket(quantity);
      setIsInBasket(true);
      setAddedSuccess(true);
      setQuantity(1);
      setTimeout(() => setAddedSuccess(false), 1500);
    } catch (error: any) {
      const rawMessage =
        error?.response?.data?.message || error?.response?.data?.error || "";

      if (
        typeof rawMessage === "string" &&
        rawMessage.toLowerCase().includes("not enough stock")
      ) {
        toast.error(t("product_out_of_stock"));
      } else {
        toast.error(t("error_adding_product"));
      }
    } finally {
      setAddLoading(false);
    }
  };

  const addToWishlist = async () => {
    if (!selectedVariantId) return;

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    setWishlistLoading(true);
    try {
      const formData = new FormData();
      formData.append("product_variant_id", selectedVariantId.toString());
      const res = await ProductsService.removeAddWishlist(formData);

      if (res.data.message.includes("Remove")) {
        setIsWishlist(false);
        decreaseWishlist(1);
      } else {
        setIsWishlist(true);
        increaseWishlist(1);
      }
    } catch (error: any) {
      console.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t("common_error"),
      );
    } finally {
      setWishlistLoading(false);
    }
  };

  const product = detail?.product;
  const variants = detail?.product_variant_list || [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  if (!product || !selectedVariant) return <Loading />;

  const viewAttributes: Record<string, string[]> = {};
  const viewAttributesMap: Record<string, Record<string, any>> = {};

  variants.forEach((variant) => {
    variant.attributes?.forEach((attr: any) => {
      if (attr.is_view) {
        if (!viewAttributes[attr.attribute_name]) {
          viewAttributes[attr.attribute_name] = [];
          viewAttributesMap[attr.attribute_name] = {};
        }
        const normalizedValue = attr.value.trim().toLowerCase();
        const existingNormalized = viewAttributes[attr.attribute_name].map(
          (v: string) => v.trim().toLowerCase(),
        );
        if (!existingNormalized.includes(normalizedValue)) {
          viewAttributes[attr.attribute_name].push(attr.value);
          viewAttributesMap[attr.attribute_name][attr.value] = attr;
        }
      }
    });
  });

  const handleVariantSelect = (attributeName: string, value: string) => {
    let foundVariant = variants.find((v: any) =>
      v.attributes?.some(
        (a: any) =>
          a.attribute_name === attributeName &&
          a.value === value &&
          v.attributes?.every((attr: any) => {
            if (attr.attribute_name === attributeName) return true;
            const currentAttr = selectedVariant.attributes?.find(
              (a: any) => a.attribute_name === attr.attribute_name,
            );
            return currentAttr ? attr.value === currentAttr.value : true;
          }),
      ),
    );

    if (!foundVariant) {
      const matchingVariants = variants.filter((v: any) =>
        v.attributes?.some(
          (a: any) => a.attribute_name === attributeName && a.value === value,
        ),
      );
      if (matchingVariants.length > 0) {
        foundVariant = [...matchingVariants].sort((a: any, b: any) => {
          if (a.stock_quantity > 0 && b.stock_quantity === 0) return -1;
          if (a.stock_quantity === 0 && b.stock_quantity > 0) return 1;
          return (a.discount_price ?? a.price) - (b.discount_price ?? b.price);
        })[0];
      }
    }

    if (foundVariant) {
      const newId = (foundVariant as any).id;
      setSelectedVariantId(newId);
      setIsWishlist((foundVariant as any).is_wish || false);
      setQuantity(1);
      updateUrlWithVariantId(newId);
      checkIsInBasket(newId);
    }
  };

  return (
    <div className={styles.product_detail}>
      <TitleUpdater title={getLocalizedName(selectedVariant)} />
      <div className="container">
        <div className={styles.content}>
          <ImageGallery
            images={selectedVariant.images || []}
            alt={getLocalizedName(selectedVariant)}
          />
          <div className={styles.info}>
            <div
              className={`${styles.stock_badge} ${
                selectedVariant.stock_quantity > 0
                  ? styles.in_stock
                  : styles.out_of_stock
              }`}
            >
              {selectedVariant.stock_quantity > 0 ? (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>
                    {selectedVariant.stock_quantity} {t("in_stock")}
                  </span>
                </>
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{t("no_stock")}</span>
                </>
              )}
            </div>
            <h1 className={styles.name}>{getLocalizedName(selectedVariant)}</h1>
            <p className={styles.description}>
              {getLocalizedDescription(selectedVariant)}
            </p>

            <div className={styles.price_wishlist_row}>
              {selectedVariant.discount_percent &&
              Number(selectedVariant.discount_percent) > 0 ? (
                <div className={styles.price_section}>
                  <p className={styles.price}>
                    {selectedVariant.discount_price} {contextCurrency}
                  </p>
                  <p className={styles.discount_price}>
                    {selectedVariant.price} {contextCurrency}
                  </p>
                </div>
              ) : (
                <div className={styles.price_section}>
                  <p className={styles.price}>
                    {selectedVariant.price} {contextCurrency}
                  </p>
                </div>
              )}

              <button
                className={styles.wishlist_btn}
                onClick={addToWishlist}
                disabled={wishlistLoading}
              >
                {wishlistLoading ? (
                  <div className={styles.loading_spinner}></div>
                ) : isWishlist ? (
                  <>
                    <FilledHeartIcon />
                    <span>{t("added_to_wishlist")}</span>
                  </>
                ) : (
                  <>
                    <HeartIcon />
                    <span>{t("add_to_wishlist")}</span>
                  </>
                )}
              </button>
            </div>

            <div className={styles.attributes}>
              {Object.entries(viewAttributes).map(([attrName, values]) => (
                <div key={attrName} className={styles.attribute_group}>
                  <label className={styles.label}>
                    {getAttributeName(viewAttributesMap[attrName][values[0]])}:
                  </label>
                  <div className={styles.options}>
                    {values.map((value) => {
                      const attr = viewAttributesMap[attrName][value];
                      const isColor = attr?.attribute_type === "color";
                      const isSelected = selectedVariant.attributes?.some(
                        (a: any) =>
                          a.attribute_name === attrName && a.value === value,
                      );
                      const isOutOfStock = !variants.some(
                        (v: any) =>
                          v.stock_quantity > 0 &&
                          v.attributes?.some(
                            (a: any) =>
                              a.attribute_name === attrName &&
                              a.value === value,
                          ),
                      );
                      const displayValue = getAttributeValue(attr);
                      const titleText = isOutOfStock
                        ? (t("out_of_stock") ?? displayValue)
                        : displayValue;

                      if (isColor) {
                        return (
                          <button
                            key={value}
                            className={`${styles.color_option} ${isSelected ? styles.active : ""}`}
                            style={{ background: value }}
                            onClick={() => handleVariantSelect(attrName, value)}
                            disabled={isOutOfStock}
                            title={titleText as string}
                          >
                            {isSelected && (
                              <span className={styles.checkmark}>✓</span>
                            )}
                          </button>
                        );
                      }

                      return (
                        <button
                          key={value}
                          className={`${styles.option} ${isSelected ? styles.active : ""}`}
                          onClick={() => handleVariantSelect(attrName, value)}
                          disabled={isOutOfStock}
                          title={
                            isOutOfStock ? (titleText as string) : undefined
                          }
                        >
                          {displayValue}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.meta_info}>
              <div className={styles.meta_item}>
                <span className={styles.rating}>⭐⭐⭐⭐⭐</span>
                <span className={styles.meta_text}>8 {t("reviews")}</span>
              </div>
              <div className={styles.separator}>|</div>
              <div className={styles.meta_item}>
                <span className={styles.meta_label}>{t("sold")}:</span>
                <span className={styles.meta_text}>35 {t("items")}</span>
              </div>
              <div className={styles.separator}>|</div>
              <div className={styles.meta_item}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 7h-3V5c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM9 5h6v2H9V5zm11 15H4V9h3v2h2V9h6v2h2V9h3v11z" />
                </svg>
                {delivery && delivery.delivery_fee > 0 ? (
                  <>
                    <span className={styles.meta_label}>{t("delivery")}:</span>
                    <span className={styles.meta_text}>
                      {delivery.delivery_fee.toFixed(2)}{" "}
                      {delivery.currency_name}
                    </span>
                  </>
                ) : (
                  <span className={styles.meta_text}>{t("free_delivery")}</span>
                )}
              </div>
            </div>

            {delivery &&
              delivery.delivery_fee > 0 &&
              delivery.free_delivery_threshold > 0 && (
                <p className={styles.delivery_note}>
                  {t("free_delivery_from", {
                    amount: delivery.free_delivery_threshold.toFixed(2),
                    currency: delivery.currency_name,
                  })}
                </p>
              )}

            <div className={styles.quantity_cart_row}>
              <div className={styles.quantity_selector}>
                <button
                  className={styles.qty_btn}
                  onClick={decreaseQuantity}
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className={styles.qty_value}>{quantity}</span>
                <button
                  className={styles.qty_btn}
                  onClick={increaseQuantity}
                  disabled={
                    selectedVariant.stock_quantity === 0 ||
                    quantity >= selectedVariant.stock_quantity
                  }
                >
                  +
                </button>
              </div>

              <Button
                className={`${styles.cart_btn} ${
                  isInBasket || addedSuccess ? styles.added_success : ""
                }`}
                onClick={addToBasket}
                loading={addLoading}
                disabled={addLoading || selectedVariant.stock_quantity === 0}
              >
                <CartIcon />
                <span>{isInBasket ? t("in_cart") : t("add_to_cart")}</span>
              </Button>
            </div>

            <InstallmentPlans
              banks={installmentBanks}
              price={Number(
                selectedVariant.discount_percent &&
                  Number(selectedVariant.discount_percent) > 0
                  ? selectedVariant.discount_price
                  : selectedVariant.price,
              )}
              currency={contextCurrency}
            />

            <div className={styles.store_badge}>
              <span className={styles.store_label}>{t("store")}</span>
              <div className={styles.store_info}>
                {product.store.logo_url ? (
                  <img src={product.store.logo_url} alt={product.store?.name} />
                ) : null}
                <span className={styles.store_name}>{product.store?.name}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.rest_content}>
          <div className={styles.sections_container}>
            <div className={styles.left_section}>
              <div className={styles.reviews_section}>
                <h3 className={styles.section_title}>
                  {t("reviews_and_rating")} (11)
                </h3>
                <div className={styles.reviews_list}>
                  {[1, 2, 3].map((review) => (
                    <div key={review} className={styles.review_card}>
                      <div className={styles.review_header}>
                        <img
                          src="https://via.placeholder.com/40"
                          alt="User"
                          className={styles.review_avatar}
                        />
                        <div className={styles.review_meta}>
                          <h4 className={styles.reviewer_name}>Elşən Rzayev</h4>
                          <div className={styles.review_rating}>
                            <span className={styles.rating_score}>5.0</span>
                            <span className={styles.stars}>⭐⭐⭐⭐⭐</span>
                            <span className={styles.review_date}>
                              1 ay əvvəl
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className={styles.review_text}>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit,
                        sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                      </p>
                    </div>
                  ))}
                </div>
                <div className={styles.pagination}>
                  <button className={styles.page_btn}>‹</button>
                  <button className={`${styles.page_btn} ${styles.active}`}>
                    1
                  </button>
                  <button className={styles.page_btn}>2</button>
                  <button className={styles.page_btn}>3</button>
                  <button className={styles.page_btn}>4</button>
                  <button className={styles.page_btn}>›</button>
                </div>
              </div>
            </div>

            <div className={styles.right_section}>
              <div className={styles.description_section}>
                <h3 className={styles.section_title}>{t("description")}</h3>
                <div className={styles.description_content}>
                  <h4 className={styles.product_subtitle}>
                    Linen (Kətan) Köynək
                  </h4>
                  <p className={styles.description_text}>
                    {getLocalizedDescription(selectedVariant) ||
                      "Yüngül və nəfəsalan kətan parçadan hazırlanmış bu köynək isti havalarda maksimum rahatlıq təmin edir. Minimal dizaynı sayəsində həm gündəlik, həm də yarı-rəsmi kombinlər üçün uyğundur. Rahat kəsimi harəkət azadlığı yaradır, təbii teksturası isə təmiz və zövqlü görünüş verir."}
                  </p>
                </div>
              </div>

              <div className={styles.details_section}>
                <h3 className={styles.section_title}>{t("details")}</h3>
                <div className={styles.details_grid}>
                  <div className={styles.detail_row}>
                    <span className={styles.detail_label}>{t("material")}</span>
                    <span className={styles.detail_value}>100% kətan</span>
                  </div>
                  <div className={styles.detail_row}>
                    <span className={styles.detail_label}>{t("season")}</span>
                    <span className={styles.detail_value}>Yaz / Yay</span>
                  </div>
                  <div className={styles.detail_row}>
                    <span className={styles.detail_label}>{t("usage")}</span>
                    <span className={styles.detail_value}>
                      Gündəlik, casual, smart-casual
                    </span>
                  </div>
                  <div className={styles.detail_row}>
                    <span className={styles.detail_label}>Fit</span>
                    <span className={styles.detail_value}>Oversize</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RelatedProducts
        categoryId={product.category?.id}
        currentProductId={product.id}
      />

      <Newsletter />

      <Modal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        btn_text={t("login") || ""}
        onSubmit={() => navigate("/auth/login")}
      >
        <div className={styles.modal}>
          <p>{t("must_login_wishlist")}</p>
        </div>
      </Modal>
    </div>
  );
};

export default ProductDetail;
